import { useCallback, useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router';

import { FWDCURVE_CATALOG_PATH, SPOT_CATALOG_PATH } from '@/config/constants';
import { countRecentlyLaunched } from '@/entities/catalog-item';
import useDocumentTitleRename from '@/shared/hooks/useDocumentTitleRename';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';

import { FactsheetDialogButton } from '@/features/factsheet';

import CatalogHero from '../components/CatalogHero/CatalogHero';
import CatalogSkeleton from '../components/CatalogSkeleton/CatalogSkeleton';
import CatalogTable from '../components/CatalogTable/CatalogTable';
import { NEW_DATA_URL_PARAM } from '../constants';
import useCatalog from '../hooks/useCatalog';
import { buildCrossCatalogPath } from '../lib/buildCrossCatalogPath';
import { FilterProvider } from '../state/filter.context';
import { useFilter } from '../state/useFilter';

const pageInformation = {
  mainPageTitle: 'CatalogTable'
};

const CatalogBase = () => {
  const location = useLocation();
  const pathname = location.pathname.toLowerCase();
  const isForwardCurves =
    pathname.includes(FWDCURVE_CATALOG_PATH) || pathname.includes('forward-curves');

  const renderFactsheetButton = useCallback(
    (code: string, mode: 'modal' | 'newTab') => (
      <FactsheetDialogButton
        code={code}
        fontSize={14}
        mode={mode}
      />
    ),
    []
  );

  const {
    status,
    filterOpen,
    setFilterOpen,
    displaySettingsOpen,
    setDisplaySettingsOpen,
    table,
    rows,
    getRowPresentation,
    onRowActivate,
    activeTab,
    expandedData,
    setExpandedData,
    forwardPeriodsInline,
    setForwardPeriodsInline,
    periodGroups,
    showDiscontinued,
    setShowDiscontinued
  } = useCatalog({ isForwardCurves, isInDashboard: false, renderFactsheetButton });

  const unit = useMemo(() => {
    if (isForwardCurves) return 'forward curve';
    return expandedData ? 'item' : 'spot code';
  }, [expandedData, isForwardCurves]);

  useDocumentTitleRename('GX Go | Catalog');
  const { filters, setFilter, clearAllFilters } = useFilter();
  const navigate = useNavigate();

  const onTabChange = useCallback(() => {
    clearAllFilters();
    void navigate(`/catalog/${isForwardCurves ? SPOT_CATALOG_PATH : FWDCURVE_CATALOG_PATH}`);
  }, [clearAllFilters, isForwardCurves, navigate]);

  const onNewClick = useCallback(() => {
    setFilter(NEW_DATA_URL_PARAM, !filters[NEW_DATA_URL_PARAM]);
  }, [filters, setFilter]);

  // Navigates to the OPPOSITE catalog view filtered to the row's code. Stable so
  // it never breaks the memoized rows.
  const onViewInOtherCatalog = useCallback(
    (code: string) => {
      void navigate(buildCrossCatalogPath(isForwardCurves, code));
    },
    [isForwardCurves, navigate]
  );

  // The "View in Spot" link is the return leg of a Spot→Forward jump, so it only
  // appears while a code filter is active (never on unfiltered browsing).
  const hasCodeFilter = Boolean(filters.code);

  if (status === 'loading') {
    return <CatalogSkeleton />;
  }

  const count = rows.length;
  const newCount = countRecentlyLaunched(
    rows.map(row => row.original),
    new Date()
  );

  return (
    <>
      <PageInformationContext value={pageInformation}>
        <CatalogHero
          activeTab={activeTab}
          count={count}
          newCount={newCount}
          unit={unit}
          onNewClick={onNewClick}
          onTabChange={onTabChange}
        />
      </PageInformationContext>

      <CatalogTable
        displaySettingsOpen={displaySettingsOpen}
        expandedData={expandedData}
        filterOpen={filterOpen}
        forwardPeriodsInline={forwardPeriodsInline}
        getRowPresentation={getRowPresentation}
        isForwardCurves={isForwardCurves}
        hasCodeFilter={hasCodeFilter}
        renderFactsheetButton={renderFactsheetButton}
        onViewInOtherCatalog={onViewInOtherCatalog}
        onRowActivate={onRowActivate}
        periodGroups={periodGroups}
        setDisplaySettingsOpen={setDisplaySettingsOpen}
        setExpandedData={setExpandedData}
        setFilterOpen={setFilterOpen}
        setForwardPeriodsInline={setForwardPeriodsInline}
        setShowDiscontinued={setShowDiscontinued}
        showDiscontinued={showDiscontinued}
        rows={rows}
        table={table}
      />
    </>
  );
};

export const CatalogPage = () => (
  <FilterProvider>
    <CatalogBase />
  </FilterProvider>
);
